/**
 * Shor vs Grover
 * Why one quantum algorithm destroys RSA and the other only dents symmetric
 * crypto. Shor turns f(x) = a^x mod N into a period the QFT reads as sharp
 * peaks; a structureless (lattice-like) signal gives the QFT nothing. Grover
 * is a separate, merely-quadratic search speedup.
 */
import {
  multiplicativeOrder,
  shorFactor,
  suggestBases,
  powerSequence,
  combSequence,
  dftMagnitude,
  makeRng,
  groverSecurity,
  factorPairs,
  PRIMITIVES,
  SEMIPRIMES,
} from '../../lib/labs/shor-grover';

// ===== DOM =====
const modeSelector = document.getElementById('sg-mode-selector') as HTMLElement;
const nSelector = document.getElementById('sg-n-selector') as HTMLElement;
const rsaControls = document.getElementById('sg-rsa-controls') as HTMLElement;
const aInput = document.getElementById('sg-a') as HTMLInputElement;
const aVal = document.getElementById('sg-a-val') as HTMLElement;
const aSuggest = document.getElementById('sg-a-suggest') as HTMLButtonElement;
const signalCanvas = document.getElementById('sg-signal-canvas') as HTMLCanvasElement;
const spectrumCanvas = document.getElementById('sg-spectrum-canvas') as HTMLCanvasElement;
const signalCap = document.getElementById('sg-signal-cap') as HTMLElement;
const spectrumCap = document.getElementById('sg-spectrum-cap') as HTMLElement;
const readout = document.getElementById('sg-readout') as HTMLElement;
const hint = document.getElementById('sg-hint') as HTMLElement;
const groverSelector = document.getElementById('grover-selector') as HTMLElement;
const groverOutput = document.getElementById('grover-output') as HTMLElement;

// ===== state =====
type Mode = 'rsa' | 'lattice';
let mode: Mode = 'rsa';
let N = 15;
let a = 7;
let groverId = 'aes128';
const noiseSeed = 1337;

const COLORS = {
  bg: '#0d1117',
  axis: 'rgba(148,163,184,0.45)',
  grid: 'rgba(148,163,184,0.10)',
  stem: '#60a5fa',
  stemHi: '#fbbf24',
  bar: '#475569',
  peak: '#fbbf24',
  lattice: '#f472b6',
};

// ===== canvas utilities =====
function dpr(): number {
  return window.devicePixelRatio || 1;
}

function sizeCanvas(c: HTMLCanvasElement): void {
  const rect = c.getBoundingClientRect();
  const d = dpr();
  c.width = rect.width * d;
  c.height = rect.height * d;
}

function clearCanvas(ctx: CanvasRenderingContext2D, c: HTMLCanvasElement): void {
  ctx.clearRect(0, 0, c.width, c.height);
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, c.width, c.height);
}

// Register length: a multiple of the period near 128 so QFT peaks are exact.
function registerLength(r: number): number {
  const mult = Math.max(1, Math.round(128 / r));
  return Math.min(192, r * mult);
}

// ===== drawing: stem plot of a sequence =====
function drawStems(
  c: HTMLCanvasElement,
  values: number[],
  maxShown: number,
  highlightPeriod: number | null,
  color: string
): void {
  const ctx = c.getContext('2d');
  if (!ctx) return;
  clearCanvas(ctx, c);
  const d = dpr();
  const padL = 8 * d;
  const padR = 8 * d;
  const padT = 10 * d;
  const padB = 18 * d;
  const w = c.width - padL - padR;
  const h = c.height - padT - padB;
  const n = Math.min(values.length, maxShown);
  const maxV = Math.max(1, ...values.slice(0, n));
  const baseY = padT + h;

  // baseline
  ctx.strokeStyle = COLORS.axis;
  ctx.lineWidth = d;
  ctx.beginPath();
  ctx.moveTo(padL, baseY);
  ctx.lineTo(padL + w, baseY);
  ctx.stroke();

  const step = w / n;
  for (let x = 0; x < n; x++) {
    const px = padL + step * (x + 0.5);
    const py = baseY - (values[x] / maxV) * h;
    const inFirstPeriod = highlightPeriod != null && x < highlightPeriod;
    ctx.strokeStyle = inFirstPeriod ? COLORS.stemHi : color;
    ctx.lineWidth = Math.max(1.2 * d, Math.min(step * 0.4, 4 * d));
    ctx.beginPath();
    ctx.moveTo(px, baseY);
    ctx.lineTo(px, py);
    ctx.stroke();
    ctx.fillStyle = inFirstPeriod ? COLORS.stemHi : color;
    ctx.beginPath();
    ctx.arc(px, py, 2.2 * d, 0, Math.PI * 2);
    ctx.fill();
  }

  // x label
  ctx.fillStyle = 'rgba(148,163,184,0.8)';
  ctx.font = `${10 * d}px ui-monospace, monospace`;
  ctx.textAlign = 'left';
  ctx.fillText('x = 0', padL, c.height - 5 * d);
  ctx.textAlign = 'right';
  ctx.fillText(`x = ${n - 1}`, padL + w, c.height - 5 * d);
}

// ===== drawing: spectrum bars (skip DC, show k = 1..M/2) =====
function drawSpectrum(
  c: HTMLCanvasElement,
  mags: number[],
  peakSpacing: number | null
): void {
  const ctx = c.getContext('2d');
  if (!ctx) return;
  clearCanvas(ctx, c);
  const d = dpr();
  const padL = 8 * d;
  const padR = 8 * d;
  const padT = 10 * d;
  const padB = 18 * d;
  const w = c.width - padL - padR;
  const h = c.height - padT - padB;
  const half = Math.floor(mags.length / 2);
  const shown = mags.slice(1, half + 1);
  const maxV = Math.max(1e-9, ...shown);
  const baseY = padT + h;
  const step = w / shown.length;

  for (let i = 0; i < shown.length; i++) {
    const k = i + 1;
    const px = padL + step * i;
    const barH = (shown[i] / maxV) * h;
    const isPeak = peakSpacing != null && k % peakSpacing === 0;
    ctx.fillStyle = isPeak ? COLORS.peak : COLORS.bar;
    ctx.fillRect(px, baseY - barH, Math.max(1.5 * d, step * 0.8), barH);
  }

  ctx.strokeStyle = COLORS.axis;
  ctx.lineWidth = d;
  ctx.beginPath();
  ctx.moveTo(padL, baseY);
  ctx.lineTo(padL + w, baseY);
  ctx.stroke();

  ctx.fillStyle = 'rgba(148,163,184,0.8)';
  ctx.font = `${10 * d}px ui-monospace, monospace`;
  ctx.textAlign = 'left';
  ctx.fillText('frequency k = 1', padL, c.height - 5 * d);
  ctx.textAlign = 'right';
  ctx.fillText(`k = ${half}`, padL + w, c.height - 5 * d);
}

// ===== readout =====
function setReadout(rows: [string, string, string?][]): void {
  readout.innerHTML = rows
    .map(
      ([k, v, tone]) =>
        `<div class="flex flex-col gap-0.5 rounded-lg border ${tone ?? 'border-border/70'} bg-surface/50 p-3">
          <span class="text-xs uppercase tracking-[0.2em] text-muted">${k}</span>
          <span class="font-mono text-sm text-text">${v}</span>
        </div>`
    )
    .join('');
}

// ===== render =====
function render(): void {
  if (mode === 'rsa') renderRsa();
  else renderLattice();
}

function renderRsa(): void {
  rsaControls.style.display = '';
  const result = shorFactor(N, a);
  const r = multiplicativeOrder(a, N);
  const M = r ? registerLength(r) : 128;
  const seq = powerSequence(a, N, M);
  const qftInput = r ? combSequence(r, M) : seq;
  const mags = dftMagnitude(qftInput);
  const peakSpacing = r ? M / r : null;

  drawStems(signalCanvas, seq, Math.min(M, 48), r, COLORS.stem);
  drawSpectrum(spectrumCanvas, mags, peakSpacing);

  signalCap.innerHTML = r
    ? `f(x) = ${a}<sup>x</sup> mod ${N}. Gold marks one period (length r = ${r}).`
    : `f(x) = ${a}<sup>x</sup> mod ${N}. a and N share a factor, so there is no clean period.`;
  spectrumCap.innerHTML = peakSpacing
    ? `After measuring f(x), the input register is a comb spaced by r. Its QFT peaks every ${peakSpacing} steps, so r = ${M}/${peakSpacing} = ${r}.`
    : `No clean periodic structure to lock onto.`;

  const factorsStr = result.factors
    ? `${result.factors[0]} x ${result.factors[1]}`
    : 'none';
  setReadout([
    ['Target', `N = ${N}`],
    ['Base', `a = ${a}`],
    ['Period r', r ? String(r) : 'n/a (gcd shortcut)'],
    [
      'Factored',
      result.factors ? `${N} = ${factorsStr}` : 'pick another a',
      result.usable || result.factors ? 'border-emerald-500/50' : 'border-orange-500/50',
    ],
  ]);

  hint.textContent = result.usable
    ? 'Period found, factors recovered. RSA at this size is broken in one shot.'
    : result.factors
      ? 'Lucky gcd shortcut split N without even needing the period.'
      : result.reason;
}

function renderLattice(): void {
  rsaControls.style.display = 'none';
  const M = 128;
  const rng = makeRng(noiseSeed);
  const seq = Array.from({ length: M }, () => Math.floor(rng() * N));
  const mags = dftMagnitude(seq);

  drawStems(signalCanvas, seq, 48, null, COLORS.lattice);
  drawSpectrum(spectrumCanvas, mags, null);

  signalCap.innerHTML =
    'A lattice / LWE problem has no global period. To the QFT it looks like this: structureless.';
  spectrumCap.innerHTML =
    'Flat noise floor, no peak to grab. Shor finds nothing, so the best quantum attack is Grover-grade search.';

  setReadout([
    ['Structure', 'none (no hidden period)'],
    ['QFT peak', 'none'],
    ['Shor', 'no foothold', 'border-emerald-500/50'],
    ['Best quantum attack', 'sieving, only ~quadratic', 'border-emerald-500/50'],
  ]);

  hint.textContent =
    'Same Fourier transform, nothing to find. This is why lattice problems resist Shor.';
}

// ===== Grover calculator =====
function renderGrover(): void {
  const prim = PRIMITIVES.find((p) => p.id === groverId)!;
  const g = groverSecurity(prim.bits);
  const barWidth = (bits: number) => `${Math.min(100, (bits / 384) * 100)}%`;

  groverOutput.innerHTML = `
    <div class="grid gap-4 sm:grid-cols-2">
      <div class="rounded-lg border border-border/70 bg-surface/50 p-4">
        <p class="text-xs uppercase tracking-[0.2em] text-muted">Classical brute force</p>
        <p class="mt-1 font-mono text-lg text-text">2<sup>${g.classicalBits}</sup> tries</p>
        <div class="mt-2 h-2 rounded bg-border/40"><div class="h-2 rounded bg-blue-400" style="width:${barWidth(g.classicalBits)}"></div></div>
      </div>
      <div class="rounded-lg border border-border/70 bg-surface/50 p-4">
        <p class="text-xs uppercase tracking-[0.2em] text-muted">With Grover</p>
        <p class="mt-1 font-mono text-lg text-text">2<sup>${g.quantumBits}</sup> tries</p>
        <div class="mt-2 h-2 rounded bg-border/40"><div class="h-2 rounded ${g.safe ? 'bg-emerald-400' : 'bg-orange-400'}" style="width:${barWidth(g.quantumBits)}"></div></div>
      </div>
    </div>
    <div class="mt-3 rounded-lg border ${g.safe ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' : 'border-orange-500/50 bg-orange-500/10 text-orange-300'} p-3 text-sm">
      ${prim.label} (${prim.note.toLowerCase()}): Grover halves the exponent to <span class="font-mono">2<sup>${g.quantumBits}</sup></span>.
      ${
        g.safe
          ? 'That still leaves a 128-bit-plus margin, so it stays safe. No algorithm change needed.'
          : 'That drops below the 128-bit margin. The fix is a bigger key, not a new algorithm.'
      }
    </div>`;

  for (const btn of groverSelector.querySelectorAll<HTMLButtonElement>('button')) {
    btn.classList.toggle('active', btn.dataset.prim === groverId);
  }
}

// ===== selectors =====
function setActive(container: HTMLElement, attr: string, value: string): void {
  for (const btn of container.querySelectorAll<HTMLButtonElement>('button')) {
    btn.classList.toggle('active', btn.dataset[attr] === value);
  }
}

function resetBaseFor(n: number): void {
  const bases = suggestBases(n);
  a = bases.length ? bases[0] : 2;
  aInput.min = '2';
  aInput.max = String(n - 1);
  aInput.value = String(a);
  aVal.textContent = String(a);
}

function buildSelectors(): void {
  const modes: [Mode, string][] = [
    ['rsa', 'Shor breaks RSA'],
    ['lattice', 'Lattices survive'],
  ];
  modeSelector.innerHTML = '';
  for (const [id, label] of modes) {
    const btn = document.createElement('button');
    btn.dataset.mode = id;
    btn.className =
      'mode-btn rounded-full border border-border/70 px-4 py-2 text-sm text-muted transition hover:border-blue-500/50 hover:text-blue-400';
    btn.textContent = label;
    btn.addEventListener('click', () => {
      mode = id;
      setActive(modeSelector, 'mode', id);
      render();
    });
    modeSelector.appendChild(btn);
  }

  nSelector.innerHTML = '';
  for (const sp of SEMIPRIMES) {
    const fp = factorPairs(sp)!;
    const btn = document.createElement('button');
    btn.dataset.n = String(sp);
    btn.className =
      'n-btn rounded-full border border-border/70 px-3 py-1.5 text-sm text-muted transition hover:border-blue-500/50 hover:text-blue-400';
    btn.textContent = String(sp);
    btn.title = `${sp} = ${fp[0]} x ${fp[1]}`;
    btn.addEventListener('click', () => {
      N = sp;
      resetBaseFor(N);
      setActive(nSelector, 'n', String(sp));
      render();
    });
    nSelector.appendChild(btn);
  }

  groverSelector.innerHTML = '';
  for (const prim of PRIMITIVES) {
    const btn = document.createElement('button');
    btn.dataset.prim = prim.id;
    btn.className =
      'grover-btn rounded-full border border-border/70 px-3 py-1.5 text-sm text-muted transition hover:border-blue-500/50 hover:text-blue-400';
    btn.textContent = prim.label;
    btn.addEventListener('click', () => {
      groverId = prim.id;
      renderGrover();
    });
    groverSelector.appendChild(btn);
  }
}

function bindControls(): void {
  aInput.addEventListener('input', () => {
    a = parseInt(aInput.value, 10);
    aVal.textContent = String(a);
    render();
  });
  aSuggest.addEventListener('click', () => {
    const bases = suggestBases(N);
    if (bases.length) {
      // rotate to the next working base for variety
      const idx = bases.indexOf(a);
      a = bases[(idx + 1) % bases.length];
      aInput.value = String(a);
      aVal.textContent = String(a);
      render();
    }
  });
}

function setupSizing(): void {
  const observer = new ResizeObserver(() => {
    sizeCanvas(signalCanvas);
    sizeCanvas(spectrumCanvas);
    render();
  });
  sizeCanvas(signalCanvas);
  sizeCanvas(spectrumCanvas);
  observer.observe(signalCanvas.parentElement!);
}

function init(): void {
  buildSelectors();
  bindControls();
  resetBaseFor(N);
  setActive(modeSelector, 'mode', mode);
  setActive(nSelector, 'n', String(N));
  setupSizing();
  render();
  renderGrover();
}

init();
